import { stateMap } from "../../utils";
import {
  TextBlock,
  EmailBlock,
  SelectBlock,
  MaskedTextBlock,
} from "../../parts/FormBlocks";
import InputMask from "react-input-mask";
import { useEffect, useState } from "react";
import classNames from "classnames";
import Api from "../../Api";
import { Company, UIValue } from "../../types";
import { Client } from "../../types";
import DiscreetMessage from "../../parts/DiscreetMessage";

type ClientsFormProps = {
  client: Client;
  buttonText: string;
  onSubmit: () => Promise<void>;
  onChange: (key: string) => (value: UIValue) => void;
  error?: boolean;
  errorDoc?: boolean;
};
const ClientsForm = ({
  client,
  buttonText,
  onSubmit,
  onChange,
  error,
  errorDoc,
}: ClientsFormProps) => {
  const [zipcodeError, setZipcodeError] = useState(false);
  const [sellers, setSellers] = useState<{ label: string; value: string }[]>(
    []
  );
  const [companies, setCompanies] = useState<Company[] | null>(null);
  const [saving, setSaving] = useState(false);

  const fillAddress = (event: React.MouseEvent) => {
    event.preventDefault();
    fetch(`https://viacep.com.br/ws/${client.zipcode}/json/`)
      .then((response) => response.json())
      .then((result) => {
        if (result.erro) {
          setZipcodeError(true);
          return;
        }

        onChange("state")(result.uf);
        onChange("city")(result.localidade);
        onChange("area")(result.bairro);
        onChange("street")(result.logradouro);
      })
      .catch(() => setZipcodeError(true));
  };

  useEffect(() => {
    Api.clientsGetSellers().then(setSellers);
    Api.getCompanies().then((companies) => [
      setCompanies(companies),
      onChange("companyUuid")(companies[0].uuid),
    ]);
  }, []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    await onSubmit();
    setSaving(false);
  };

  if (!companies) {
    return <DiscreetMessage text="Carregando ..." />;
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={submit}>
      <div className="flex py-4 items-center">
        <span className="text-2xl font-extrabold flex-1">Cliente</span>

        <div className="flex border rounded-xl font-bold text-sm">
          <label className="py-2 px-6 flex gap-3 border-r">
            <input
              type="radio"
              value="pf"
              checked={client.type === "pf"}
              onChange={(event) => [
                onChange("document")(""),
                onChange("type")(event.target.value),
              ]}
            />
            <span>PF</span>
          </label>

          <label className="py-2 px-6 flex gap-3">
            <input
              type="radio"
              value="pj"
              checked={client.type === "pj"}
              onChange={(event) => [
                onChange("document")(""),
                onChange("type")(event.target.value),
              ]}
            />
            <span>PJ</span>
          </label>
        </div>
      </div>

      <TextBlock
        label={"Nome"}
        value={client.name}
        onChange={onChange("name")}
      />

      <MaskedTextBlock
        label={client.type === "pf" ? "CPF" : "CNPJ"}
        value={client.document}
        onChange={onChange("document")}
        mask={client.type === "pf" ? "999.999.999-99" : "99.999.999/9999-99"}
        error={errorDoc}
      />

      <SelectBlock
        label={"Empresa"}
        value={client.companyUuid}
        onChange={onChange("companyUuid")}
        options={companies.map((company) => ({
          label: company.shortName,
          value: company.uuid,
        }))}
      />

      <SelectBlock
        label={"Consultor"}
        value={client.sellerUuid}
        onChange={onChange("sellerUuid")}
        options={sellers}
      />

      <div className="text-2xl font-extrabold py-4">Contato</div>

      <TextBlock
        label={"Pessoa de contato"}
        value={client.contactName}
        onChange={onChange("contactName")}
      />

      <EmailBlock
        label={"Email"}
        value={client.email}
        onChange={onChange("email")}
      />

      <MaskedTextBlock
        label={"Celular"}
        value={client.mobile}
        onChange={onChange("mobile")}
        mask="(99) 99999-9999"
        optional={true}
      />

      <MaskedTextBlock
        label={"Telefone fixo"}
        value={client.phone}
        onChange={onChange("phone")}
        mask="(99) 9999-9999"
        optional={true}
      />

      <div className="text-2xl font-extrabold py-4">Endereço</div>

      <div className="flex flex-col gap-1">
        <label className="text-sm font-bold">CEP</label>
        <div className="flex gap-2">
          <InputMask
            className={classNames("border rounded py-2 px-3 max-w-[150px]", {
              "border-red-700 bg-red-50": zipcodeError,
            })}
            mask="99999-999"
            value={client.zipcode}
            onChange={(event) => [
              setZipcodeError(false),
              onChange("zipcode")(event.target.value),
            ]}
            required
          />

          <button
            className="text-sm rounded hover:bg-slate-50 font-semibold p-2"
            onClick={fillAddress}
            type="button"
          >
            Preencher endereço
          </button>
        </div>
      </div>

      <SelectBlock
        label={"Estado"}
        value={client.state}
        onChange={onChange("state")}
        options={stateMap}
      />

      <TextBlock
        label={"Cidade"}
        value={client.city}
        onChange={onChange("city")}
      />

      <TextBlock
        label={"Bairro"}
        value={client.area}
        onChange={onChange("area")}
      />

      <TextBlock
        label={"Logradouro"}
        value={client.street}
        onChange={onChange("street")}
      />

      <TextBlock
        label={"Número"}
        value={client.number}
        onChange={onChange("number")}
      />

      <TextBlock
        label={"Complemento"}
        value={client.complement}
        onChange={onChange("complement")}
        optional={true}
      />

      <hr />

      {error && (
        <div className="text-sm text-red-700 font-bold">
          Erro ao cadastrar cliente
        </div>
      )}

      <div className="text-right">
        <button
          className="rounded py-2 px-4 bg-sky-600 hover:bg-sky-500 font-extrabold text-white"
          disabled={saving}
        >
          {buttonText}
        </button>
      </div>
    </form>
  );
};

export default ClientsForm;
